import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import useMicrophoneStatus from "../hooks/useMicrophoneStatus";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useEffect } from "react";
import { debugLog } from "../services/debugLog";


const MicTestBox = ({ onContinue, t, skipMicTest }) => {
  const { isAvailable, permission, error, checking } = useMicrophoneStatus();

  const micReady = isAvailable && permission === "granted";

  const handleRetry = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      window.location.reload(); // fuerza re-render con permisos actualizados
    } catch (err) {
      debugLog("❌ No se pudo acceder al micrófono:", err);
    }
  };

  useEffect(() => {
    if (skipMicTest) {
      setTimeout(() => {
        onContinue(); // cerrar automáticamente sin pedir permiso
      }, 500);
    }
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#212121",
        color: "#fff",
        borderRadius: 4,
        p: 4,
        maxWidth: 400,
        width: "100%",
        textAlign: "center",
        boxShadow: "0 0 20px rgba(0,0,0,0.5)",
        mx: "auto",
      }}
    >
      <Typography variant="h5" gutterBottom>
        {t("mic_test_box.title") || "Mic Test"}
      </Typography>

      {checking ? (
        <Box sx={{ mt: 3 }}>
          <CircularProgress size={32} sx={{ color: "#76ff03" }} />
          <Typography sx={{ mt: 2 }}>
            {t("mic_test_box.checking") || "Checking microphone..."}
          </Typography>
        </Box>
      ) : micReady ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: "#76ff03", fontWeight: 500 }}>
            {t("mic_test_box.ready") || "Microphone is ready ✅"}
          </Typography>
          <Button
            variant="contained"
            onClick={onContinue}
            sx={{
              mt: 3,
              borderRadius: 999,
              py: 1.2,
              background: "linear-gradient(to right, #43a047, #76ff03)",
              color: "#000",
              fontWeight: 600,
            }}
          >
            {t("mic_test_box.continue") || "Continue"}
          </Button>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography color="error" sx={{ fontWeight: 500 }}>
            {t("mic_test_box.blocked") || "Microphone not available or blocked"}
          </Typography>
          {error && (
            <Typography variant="body2" sx={{ mt: 1, color: "#ccc" }}>
              {error === "ENUMERATE_NOT_AVAILABLE"
                ? t("mic_test_box.error_enumerate")
                : error}
            </Typography>
          )}
            <Typography
            sx={{
                mt: 2,
                color: "#ccc",
                fontSize: 15,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                textAlign: "center"
            }}
            >
            <InfoOutlinedIcon sx={{ fontSize: 26 }} />
            {t("mic_test_box.permission_hint") ||
                'Necesitamos permiso para acceder al micrófono. Hacé clic en "Reintentar" y aceptá el permiso si aparece.'}
            </Typography>
            <Button
            onClick={handleRetry}
            variant="outlined"
            sx={{
                mt: 3,
                borderRadius: 999,
                color: "#fff",
                borderColor: "#fff",
                "&:hover": {
                borderColor: "#76ff03",
                color: "#76ff03",
                },
            }}
            >
            {t("mic_test_box.retry") || "Retry"}
            </Button>
            <Typography
                variant="body2"
                sx={{
                    mt: 4,
                    fontSize: 14,
                    color: "#ccc",
                    textAlign: "center"
                }}
                >
                {t("mic_test_box.need_help") || "¿Seguís teniendo problemas?"}{" "}
                <Box
                    component={RouterLink}
                    to="/contact"
                    sx={{
                        color: "#fff",
                        fontWeight: 600,
                        textDecoration: "underline",
                        transition: "color 0.2s ease-in-out",
                        "&:hover": { color: "#76ff03" },
                        "&:active": { color: "#76ff03" }
                    }}
                    >
                    {t("mic_test_box.contact_support") || "Contactanos"}
                </Box>
            </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MicTestBox;
